import { Card, HStack, Icon, Stack, Text } from "@chakra-ui/react";
import { t } from "i18next";
import { LifeCycleStage } from "../../../../../cra/models/LifeCycleStage";
import { useStores } from "../../../../../shared/stores/RootStore";
import { LifeCycleStageControls } from "../cra-way/LifeCycleStageControls";
import { ChatIcon, CheckIcon } from "../../../../../shared/views/icons";
import { ScoringBubble } from "../../../../../coding/views/ScoringBubble";
import { CraWay } from "../../../../../cra/models/CraWay";
import { observer } from "mobx-react";
import { formatDistanceByUnit } from "../../../../../shared/utils/format-utils";
import { DistanceUnit } from "@keita/lanepatrol-sdk";

const i18prefix = "map:bottomBar.routeItem";
const doneColor = "primary.600";
const neutralColor = "gray.400";
const errorColor = "red.400";

const CraWayItemBase: React.FC<{
  craWay: CraWay;
}> = ({ craWay }) => {
  const { craWayStore, mapStore, surveyStore } = useStores();
  const { stats, lifeCycleStage } = craWay;

  const distanceUnit = surveyStore.distanceUnit;

  return (
    <Card.Root variant={"outline"}>
      <Stack gap={0}>
        <Stack
          gap={1}
          p={3}
          onClick={() => {
            craWayStore.select(craWay.id);
            mapStore.recenterMap();
          }}
          cursor="pointer"
          _hover={{
            backgroundColor: "gray.50",
          }}
        >
          <HStack>
            <Text flexGrow={1} fontWeight="semibold" fontSize={"sm"}>
              {craWay.name}
            </Text>
            {!!craWay.score && <ScoringBubble score={craWay.score} />}
          </HStack>

          <Stack fontSize="xs" gap={1} flexGrow={1}>
            {(lifeCycleStage === LifeCycleStage.DataCollection ||
              lifeCycleStage === LifeCycleStage.Completed) && (
              <HStack>
                <Icon
                  as={CheckIcon}
                  color={
                    lifeCycleStage === LifeCycleStage.DataCollection &&
                    stats!.nNodes
                      ? doneColor
                      : neutralColor
                  }
                />
                <Text>
                  {t(`${i18prefix}.nNodes`, {
                    value: stats!.nNodes,
                  })}
                </Text>
              </HStack>
            )}

            {(lifeCycleStage !== LifeCycleStage.Design ||
              !!craWay.stats?.totalDistance) && (
              <HStack>
                <Icon as={CheckIcon} color={neutralColor} />
                <Text>
                  {t(`${i18prefix}.totalDistance`, {
                    value: formatDistanceByUnit(
                      craWay.stats?.totalDistance ?? 0,
                      distanceUnit,
                    ),
                    unit:
                      distanceUnit === DistanceUnit.Imperial ? "mi" : "km",
                  })}
                </Text>
              </HStack>
            )}

            {(lifeCycleStage === LifeCycleStage.Design ||
              lifeCycleStage === LifeCycleStage.DataCollection) && (
              <HStack>
                <Icon
                  as={CheckIcon}
                  color={craWay.plannedDistance ? doneColor : neutralColor}
                />
                <Text>
                  {t(`${i18prefix}.plannedDistance`, {
                    value: formatDistanceByUnit(
                      craWay.plannedDistance,
                      distanceUnit,
                    ),
                    unit:
                      distanceUnit === DistanceUnit.Imperial ? "mi" : "km",
                  })}
                </Text>
              </HStack>
            )}

            {(lifeCycleStage === LifeCycleStage.Coding ||
              lifeCycleStage === LifeCycleStage.Review) && (
              <HStack>
                <Icon
                  as={CheckIcon}
                  color={
                    stats!.nNodesCoded === stats!.nNodes
                      ? doneColor
                      : errorColor
                  }
                />
                <Text>
                  {t(`${i18prefix}.nodesCoded`, {
                    nNodesCoded: stats!.nNodesCoded,
                    nNodes: stats!.nNodes,
                  })}
                </Text>
              </HStack>
            )}

            {(lifeCycleStage === LifeCycleStage.Coding ||
              lifeCycleStage === LifeCycleStage.Review) && (
              <HStack>
                <Icon
                  as={ChatIcon}
                  color={stats!.nActiveComments ? doneColor : neutralColor}
                />
                <Text>
                  {t(`${i18prefix}.comments`, {
                    nActiveComments: stats!.nActiveComments,
                  })}
                </Text>
              </HStack>
            )}
          </Stack>
        </Stack>

        <Stack p={2}>
          <LifeCycleStageControls craWay={craWay} size="xs" />
        </Stack>
      </Stack>
    </Card.Root>
  );
};

export const CraWayItem = observer(CraWayItemBase);
